import classNames from 'classnames';

import OrderHistoryIcon from '../assets/icons/order_history.svg?react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, generatePath } from 'react-router-dom';

import { OrderApi } from '../api';
import { PrivateRoute } from '../components';
import { RoutePath } from '../config';
import { usePagination } from '../hooks';
import { DoctorInnerPageLayout } from '../layouts';
import { formatDateRu, formatMoney, getOrderStatusConfig } from '../utils';

export const DoctorOrderHistoryPage = () => {
  const [searchText, setSearchText] = useState('');
  const { data: allOrders = [] } = useQuery({
    keepPreviousData: true,
    queryFn: () => OrderApi.getAll(false, searchText),
    queryKey: [OrderApi.KEY, 'getAll', false, searchText],
  });
  const sortedOrders = useMemo(
    () =>
      [...allOrders].sort((a, b) => {
        const tA = new Date(a.status_date || 0).getTime();
        const tB = new Date(b.status_date || 0).getTime();
        return tB - tA;
      }),
    [allOrders],
  );
  const pagination = usePagination(sortedOrders.length);

  return (
    <PrivateRoute redirectPath={RoutePath.DoctorLogin}>
      <DoctorInnerPageLayout>
        <div className="size-full overflow-hidden rounded-[16px] bg-brand-6">
          <header className="flex h-[106px] gap-x-[24px] p-[24px]">
            <div className="relative max-w-md flex-1">
              <input
                className={classNames(
                  'input w-full pr-[90px] transition-none',
                  {
                    'input--search': !searchText,
                  },
                )}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Введите ФИО клиента или № заказа"
                type="text"
                value={searchText}
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => setSearchText('')}
                  className="absolute right-[6px] top-1/2 flex size-[40px] -translate-y-1/2 items-center justify-center rounded-full text-brand-1/60 hover:bg-brand-2 hover:text-brand-1"
                  aria-label="Очистить"
                >
                  <svg
                    className="size-[20px]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
            <Link
              className="button button--primary px-[24px]"
              to={RoutePath.DoctorCreateOrder}
            >
              Собрать заказ
            </Link>
          </header>
          <div className="flex size-full max-h-[calc(100%_-_106px)] flex-col overflow-auto rounded-[16px] bg-white p-[24px]">
            {sortedOrders.length === 0 ? (
              <div className="flex size-full flex-col items-start justify-center gap-[10px] self-center rounded-[12px] px-[16px] py-[24px] outline outline-[.9] outline-offset-[-1px] outline-[#151515]/10">
                <div className="flex w-full flex-col items-center justify-center gap-[32px]">
                  <div className="inline-flex items-center justify-start gap-[10px] rounded-[100px] bg-[#f1f7fa] p-[12px]">
                    <OrderHistoryIcon className="size-[24px] text-[#0788f1]" />
                  </div>
                  <div className="flex w-full flex-col items-start gap-[8px]">
                    <div className="w-full text-center font-['Ubuntu'] text-[20px] font-medium leading-[28px] text-[#151515]">
                      У вас пока нет ни одного заказа
                    </div>
                    <div className="w-full text-center font-['Ubuntu'] text-[14px] font-normal leading-[20px] text-[#151515] opacity-50">
                      Здесь будут отображаться заказы ваших клиентов. Соберите
                      первый заказ по кнопке выше.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full overflow-x-auto">
                <table
                  className={classNames('table min-w-[600px]', {
                    long: pagination.isActive,
                  })}
                >
                  <thead>
                    <tr>
                      <th className="whitespace-nowrap text-left">
                        Номер заказа
                      </th>
                      <th className="whitespace-nowrap text-left">
                        Дата заказа
                      </th>
                      <th className="whitespace-nowrap text-left">Лекарства</th>
                      <th className="whitespace-nowrap text-left">Цена</th>
                      <th className="whitespace-nowrap text-left">Статус</th>
                      <th className="whitespace-nowrap text-left">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOrders
                      .slice(pagination.begin, pagination.end)
                      .map((order) => {
                        const pathToOrderItem = generatePath(
                          RoutePath.DoctorOrderHistoryItem,
                          {
                            orderId: order.id.toString(),
                          },
                        );

                        return (
                          <tr key={order.id}>
                            <td className="whitespace-nowrap">
                              <Link
                                className="underline hover:no-underline"
                                to={pathToOrderItem}
                              >
                                №{order.id}
                              </Link>
                            </td>
                            <td className="whitespace-nowrap">
                              {formatDateRu(order.status_date, 'dd.MM.yyyy')}
                            </td>
                            <td className="whitespace-nowrap underline">
                              {order.medicine_count}
                            </td>
                            <td className="whitespace-nowrap">
                              {formatMoney(order.total_price ?? order.price)}
                            </td>
                            <td className="whitespace-nowrap">
                              {(() => {
                                const cfg = getOrderStatusConfig(
                                  order.status?.value ?? '',
                                );
                                return (
                                  <span
                                    className={`inline-flex items-center rounded-[100px] px-[12px] py-[4px] text-[14px] font-medium ${cfg.bg} ${cfg.text}`}
                                  >
                                    {cfg.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="whitespace-nowrap text-brand-5 underline hover:no-underline">
                              <Link to={pathToOrderItem}>Детали</Link>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                  {pagination.isActive && (
                    <tfoot>
                      <tr>
                        <td colSpan={6}>
                          <button
                            className="font-[16px] text-brand-5 underline hover:no-underline"
                            onClick={pagination.forward}
                            type="button"
                          >
                            Смотреть больше
                          </button>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </div>
        </div>
      </DoctorInnerPageLayout>
    </PrivateRoute>
  );
};
